import React, { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import API from "../services/api";
import { resolveProfilePhoto as resolveAssetUrl } from "../utils/profilePhoto";
import "./SupplierDispatchForm.css";

const DEFAULT_QUALITY_CHECKS = [
  { label: "IR64 PADDY - MOISTURE", percent: "14", unloadingReport: "" },
  { label: "FOREIGN MATTER", percent: "1", unloadingReport: "" },
  { label: "DAMAGED, DISCOLOURED", percent: "3", unloadingReport: "" },
  { label: "RECOVERY RICE", percent: "65", unloadingReport: "" },
  { label: "IMMATURE, SHRUNKEN AND SHRIVELLED", percent: "3", unloadingReport: "" },
  { label: "ADMIXTURE OF LOWES CLASS", percent: "5", unloadingReport: "" },
];

const defaultInspectionReport = () => ({
  formType: "standard",
  qualityChecks: DEFAULT_QUALITY_CHECKS.map((item) => ({ ...item })),
  vehicleNo: "",
  weight: "",
  shortWeight: "",
  finalWeight: "",
  inspectionDate: "",
});

const defaultForm = () => ({
  productId: "",
  productName: "",
  dispatchDateTime: "",
  materialName: "",
  quantity: "",
  unit: "kg",
  lotNumber: "",
  batchNumber: "",
  purchaseOrderNo: "",
  vehicleType: "",
  vehicleNumber: "",
  driverName: "",
  driverMobile: "",
  driverLicenseNo: "",
  transporterName: "",
  sealNumber: "",
  packagesCount: "",
  grossWeight: "",
  netWeight: "",
  inspectionReport: defaultInspectionReport(),
});

const resolveDispatchFormType = (product = {}, materialName = "") => {
  const configured = String(product?.dispatchFormType || "").trim().toLowerCase();
  const name = `${product?.name || ""} ${materialName || ""}`.toLowerCase();
  if (configured === "paddy" || /\bpaddy\b/.test(name)) return "paddy";
  return "standard";
};

const SupplierDispatchForm = () => {
  const [dispatchId, setDispatchId] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [files, setFiles] = useState({
    invoice: null,
    eWayBill: null,
    deliveryChallan: null,
    weighmentSlip: null,
    loadingPhotos: [],
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [poInfo, setPoInfo] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    const loadDraft = async () => {
      if (!id) {
        setDispatchId(null);
        return;
      }
      try {
        const res = await API.get(`/supplier/dispatches/${id}`);
        const d = res.data || {};
        setDispatchId(d._id || null);
        setForm({
          productId: d.productId || "",
          productName: d.productName || "",
          dispatchDateTime: d.dispatchDateTime ? new Date(d.dispatchDateTime).toISOString().slice(0, 16) : "",
          materialName: d.materialName || "",
          quantity: d.quantity ?? "",
          unit: d.unit || "kg",
          lotNumber: d.lotNumber || "",
          batchNumber: d.batchNumber || "",
          purchaseOrderNo: d.purchaseOrderNo || "",
          vehicleType: d.vehicle?.type || "",
          vehicleNumber: d.vehicle?.number || "",
          driverName: d.vehicle?.driverName || "",
          driverMobile: d.vehicle?.driverMobile || "",
          driverLicenseNo: d.vehicle?.driverLicenseNo || "",
          transporterName: d.vehicle?.transporterName || "",
          sealNumber: d.vehicle?.sealNumber || "",
          packagesCount: d.loading?.packagesCount ?? "",
          grossWeight: d.loading?.grossWeight ?? "",
          netWeight: d.loading?.netWeight ?? "",
          inspectionReport: {
            ...defaultInspectionReport(),
            ...(d.inspectionReport || {}),
            qualityChecks:
              d.inspectionReport?.qualityChecks?.length
                ? d.inspectionReport.qualityChecks.map((item) => ({
                    label: item.label || "",
                    percent: item.percent ?? "",
                    unloadingReport: item.unloadingReport || "",
                  }))
                : defaultInspectionReport().qualityChecks,
            inspectionDate: d.inspectionReport?.inspectionDate
              ? new Date(d.inspectionReport.inspectionDate).toISOString().slice(0, 10)
              : "",
          },
        });
      } catch (e) {
        setError(e?.response?.data?.message || "Failed to load dispatch");
      }
    };
    loadDraft();
  }, [id]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await API.get("/supplier/products");
        setProducts(Array.isArray(res.data) ? res.data : []);
      } catch (_e) {
        setProducts([]);
      }
    };
    loadProducts();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await API.get("/supplier/profile");
        setPoInfo(res.data?.po || null);
        const materialFromProfile = res.data?.materialName;
        setForm((prev) => ({
          ...prev,
          purchaseOrderNo: prev.purchaseOrderNo || res.data?.po?.poNumber || "",
          materialName: prev.materialName || materialFromProfile || "",
        }));
      } catch {
        setPoInfo(null);
      }
    };
    loadProfile();
  }, []);

  useEffect(() => {
    const nextProduct = products.find((item) => item._id === form.productId) || null;
    setSelectedProduct(nextProduct);
  }, [form.productId, products]);

  useEffect(() => {
    if (!selectedProduct) return;
    setForm((prev) => ({
      ...prev,
      productName: selectedProduct.name || "",
      materialName: selectedProduct.name || prev.materialName,
      unit: selectedProduct.unit ? String(selectedProduct.unit).toLowerCase() : prev.unit,
      inspectionReport: {
        ...(prev.inspectionReport?.formType === resolveDispatchFormType(selectedProduct, prev.materialName)
          ? prev.inspectionReport
          : defaultInspectionReport()),
        formType: resolveDispatchFormType(selectedProduct, prev.materialName),
        vehicleNo: prev.inspectionReport?.vehicleNo || prev.vehicleNumber || "",
      },
    }));
  }, [selectedProduct]);

  const cropYearOptions = (() => {
    const start = 2010;
    const current = new Date().getFullYear();
    const end = current + 1;
    const list = [];
    for (let y = start; y <= end; y += 1) {
      list.push(`${y}-${y + 1}`);
    }
    return list;
  })();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === "productId") {
        const product = products.find((item) => item._id === value) || null;
        return {
          ...prev,
          productId: value,
          productName: product?.name || "",
          materialName: product?.name || "",
          unit: product?.unit ? String(product.unit).toLowerCase() : prev.unit,
          inspectionReport: {
            ...defaultInspectionReport(),
            formType: resolveDispatchFormType(product, product?.name || ""),
            vehicleNo: prev.vehicleNumber || "",
          },
        };
      }

      if (name === "materialName") {
        return {
          ...prev,
          materialName: value,
          inspectionReport: {
            ...(resolveDispatchFormType(selectedProduct || {}, value) === prev.inspectionReport?.formType
              ? prev.inspectionReport
              : defaultInspectionReport()),
            formType: resolveDispatchFormType(selectedProduct || {}, value),
            vehicleNo: prev.inspectionReport?.vehicleNo || prev.vehicleNumber || "",
          },
        };
      }

      if (name === "vehicleNumber") {
        return {
          ...prev,
          vehicleNumber: value,
          inspectionReport: {
            ...prev.inspectionReport,
            vehicleNo: value,
          },
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleFileChange = (e) => {
    const { name, files: selected } = e.target;
    if (name === "loadingPhotos") {
      setFiles((prev) => ({ ...prev, loadingPhotos: Array.from(selected || []) }));
    } else {
      setFiles((prev) => ({ ...prev, [name]: selected?.[0] || null }));
    }
  };

  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : null;
  };

  const normalizeUnit = (unit) => String(unit || "").trim().toLowerCase();
  const isTon = (u) => ["ton", "tons", "mt", "tonne", "tonnes", "metric ton", "metric tons"].includes(u);
  const isKg = (u) => ["kg", "kgs", "kilogram", "kilograms"].includes(u);

  const convertQty = (qty, fromUnit, toUnit) => {
    if (!Number.isFinite(qty)) return null;
    const from = normalizeUnit(fromUnit);
    const to = normalizeUnit(toUnit);
    if (!to || from === to) return qty;
    if (isTon(to) && isKg(from)) return qty / 1000;
    if (isKg(to) && isTon(from)) return qty * 1000;
    return qty;
  };

  const computeDispatchValue = () => {
    const price = toNumber(poInfo?.pricePerUnit);
    const qty = toNumber(form.quantity);
    if (price == null || qty == null) return null;
    const unit = poInfo?.unit || form.unit;
    const qtyInPoUnit = convertQty(qty, form.unit, unit);
    if (qtyInPoUnit == null) return null;
    return Math.round(qtyInPoUnit * price * 100) / 100;
  };

  const isPaddyDispatch =
    resolveDispatchFormType(selectedProduct || {}, form.materialName || form.productName) === "paddy" ||
    form.inspectionReport?.formType === "paddy";

  const saveDraft = async () => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      if (!dispatchId) {
        const res = await API.post("/supplier/dispatches", form);
        setDispatchId(res.data?.dispatch?._id || null);
      } else {
        await API.put(`/supplier/dispatches/${dispatchId}`, form);
      }
      setMessage("Draft saved");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save draft");
    } finally {
      setLoading(false);
    }
  };

  const submitDispatch = async () => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      let idToSubmit = dispatchId;
      if (!idToSubmit) {
        const res = await API.post("/supplier/dispatches", form);
        idToSubmit = res.data?.dispatch?._id || null;
        setDispatchId(idToSubmit);
      } else {
        await API.put(`/supplier/dispatches/${idToSubmit}`, form);
      }
      if (!idToSubmit) throw new Error("Failed to create dispatch");

      const hasAttachments =
        files.invoice ||
        files.eWayBill ||
        files.deliveryChallan ||
        files.weighmentSlip ||
        (files.loadingPhotos?.length || 0) > 0;
      if (hasAttachments) {
        const fd = new FormData();
        if (files.invoice) fd.append("invoice", files.invoice);
        if (files.eWayBill) fd.append("eWayBill", files.eWayBill);
        if (files.deliveryChallan) fd.append("deliveryChallan", files.deliveryChallan);
        if (files.weighmentSlip) fd.append("weighmentSlip", files.weighmentSlip);
        if (files.loadingPhotos?.length) {
          files.loadingPhotos.forEach((f) => fd.append("loadingPhotos", f));
        }
        await API.post(`/supplier/dispatches/${idToSubmit}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await API.patch(`/supplier/dispatches/${idToSubmit}/submit`);
      setMessage("Dispatch submitted for admin approval");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Submit failed";
      const missing = e?.response?.data?.missing;
      setError(missing?.length ? `${msg}. Missing: ${missing.join(", ")}` : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sdf-page">
      <div className="sdf-container">
        <div style={{ marginBottom: 20 }}>
          <h1 className="sdf-title">Create New Dispatch</h1>
          <p className="sdf-subtitle">Admin ke products yahan dikhengay. Product select karte hi relevant dispatch format khulega.</p>
        </div>

        {message && <Alert variant="success">{message}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="sdf-banner-grid">
          <div className="sdf-banner sdf-banner-po">
            <div>
              <div className="sdf-banner-title">Purchase Order Alert</div>
              {poInfo?.totalQty != null ? (
                <div className="sdf-banner-text">
                  Linked to {poInfo.poNumber || "—"}. Remaining {poInfo.remainingQty ?? "—"} {poInfo.unit || "ton"} at Rs {poInfo.pricePerUnit ?? "—"} / {poInfo.unit || "ton"}.
                </div>
              ) : (
                <div className="sdf-banner-text">PO not set by admin. Please contact admin before submitting.</div>
              )}
            </div>
            <span className="material-symbols-outlined">assignment</span>
          </div>
          <div className="sdf-banner sdf-banner-value">
            <div>
              <div className="sdf-banner-title">Dispatch Value</div>
              <div className="sdf-banner-amount">Rs {computeDispatchValue() ?? "—"}</div>
            </div>
            <span className="material-symbols-outlined">payments</span>
          </div>
        </div>

        <form className="space-y-8">
          <section className="sdf-section">
            <div className="sdf-section-header">
              <span className="material-symbols-outlined">inventory_2</span>
              Material Details
            </div>
            <div className="sdf-section-body sdf-grid">
              <div className="sdf-field">
                <label>Product</label>
                <select name="productId" value={form.productId} onChange={handleChange}>
                  <option value="">Select product</option>
                  {products.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sdf-field">
                <label>Dispatch Date & Time</label>
                <input type="datetime-local" name="dispatchDateTime" value={form.dispatchDateTime} onChange={handleChange} />
              </div>
              <div className="sdf-field">
                <label>Material Name</label>
                <input name="materialName" value={form.materialName} onChange={handleChange} placeholder="Selected product name" />
              </div>
              <div className="sdf-field">
                <label>Quantity (Units)</label>
                <input type="number" name="quantity" value={form.quantity} onChange={handleChange} placeholder="500" />
              </div>
              <div className="sdf-field">
                <label>Unit</label>
                <input name="unit" value={form.unit} onChange={handleChange} placeholder="kg / pcs" />
              </div>
              <div className="sdf-field">
                <label>Crop Year</label>
                <select name="lotNumber" value={form.lotNumber} onChange={handleChange}>
                  <option value="">Select year</option>
                  {cropYearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="sdf-field">
                <label>Batch Number</label>
                <input name="batchNumber" value={form.batchNumber} onChange={handleChange} placeholder="BT-2025-A" />
              </div>
              <div className="sdf-field">
                <label>Purchase Order No</label>
                <input name="purchaseOrderNo" value={form.purchaseOrderNo} onChange={handleChange} placeholder="PO-2025-001" />
              </div>
            </div>
          </section>

          {selectedProduct ? (
            <section className="sdf-section">
              <div className="sdf-section-header">
                <span className="material-symbols-outlined">deployed_code</span>
                Product Selection
              </div>
              <div className="sdf-section-body">
                <div className="sdf-product-card">
                  <div>
                    <div className="sdf-product-name">{selectedProduct.name}</div>
                    <div className="sdf-product-meta">
                      <span>{selectedProduct.unit}</span>
                      <span>{isPaddyDispatch ? "Paddy quality check by QA" : "Standard dispatch format"}</span>
                      <span>GST {selectedProduct.gstPercent ?? 0}%</span>
                    </div>
                    {selectedProduct.description ? <p className="sdf-product-desc">{selectedProduct.description}</p> : null}
                  </div>
                  {selectedProduct.galleryImages?.[0]?.imageUrl ? (
                    <div className="sdf-product-preview">
                      <img src={resolveAssetUrl(selectedProduct.galleryImages[0].imageUrl)} alt={selectedProduct.name} />
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          <section className="sdf-section">
            <div className="sdf-section-header">
              <span className="material-symbols-outlined">local_shipping</span>
              Logistics Details
            </div>
            <div className="sdf-section-body sdf-grid-3">
              <div className="sdf-field">
                <label>Vehicle Type</label>
                <input name="vehicleType" value={form.vehicleType} onChange={handleChange} placeholder="Truck / Tempo" />
              </div>
              <div className="sdf-field">
                <label>Vehicle Number</label>
                <input name="vehicleNumber" value={form.vehicleNumber} onChange={handleChange} placeholder="AB-12-CD-3456" />
              </div>
              <div className="sdf-field">
                <label>Driver Name</label>
                <input name="driverName" value={form.driverName} onChange={handleChange} placeholder="John Doe" />
              </div>
              <div className="sdf-field">
                <label>Driver Mobile</label>
                <input name="driverMobile" value={form.driverMobile} onChange={handleChange} placeholder="9876543210" />
              </div>
              <div className="sdf-field">
                <label>Driver License No</label>
                <input name="driverLicenseNo" value={form.driverLicenseNo} onChange={handleChange} placeholder="DL-123456" />
              </div>
              <div className="sdf-field">
                <label>Transporter Name</label>
                <input name="transporterName" value={form.transporterName} onChange={handleChange} placeholder="Global Logistics" />
              </div>
              <div className="sdf-field">
                <label>Vehicle Seal Number</label>
                <input name="sealNumber" value={form.sealNumber} onChange={handleChange} placeholder="SEAL-2026-001" />
              </div>
            </div>
          </section>

          <section className="sdf-section">
            <div className="sdf-section-header">
              <span className="material-symbols-outlined">precision_manufacturing</span>
              Loading Details
            </div>
            <div className="sdf-section-body sdf-grid-4">
              <div className="sdf-field">
                <label>No. of Packages</label>
                <input type="number" name="packagesCount" value={form.packagesCount} onChange={handleChange} placeholder="24" />
              </div>
              <div className="sdf-field">
                <label>Gross Weight (Kg)</label>
                <input type="number" name="grossWeight" value={form.grossWeight} onChange={handleChange} placeholder="1300" />
              </div>
              <div className="sdf-field">
                <label>Net Weight (Kg)</label>
                <input type="number" name="netWeight" value={form.netWeight} onChange={handleChange} placeholder="1250" />
              </div>
            </div>
          </section>

          <section className="sdf-section">
            <div className="sdf-section-header">
              <span className="material-symbols-outlined">upload_file</span>
              Documents & Photos
            </div>
            <div className="sdf-section-body sdf-grid">
              <div className="sdf-drop">
                <h4>Upload Invoice</h4>
                <p>PDF, JPG, PNG up to 10MB</p>
                <input type="file" name="invoice" onChange={handleFileChange} />
              </div>
              <div className="sdf-drop">
                <h4>Upload E-Way Bill</h4>
                <p>PDF, JPG, PNG up to 10MB</p>
                <input type="file" name="eWayBill" onChange={handleFileChange} />
              </div>
              <div className="sdf-drop">
                <h4>Delivery Challan</h4>
                <p>PDF, JPG, PNG up to 10MB</p>
                <input type="file" name="deliveryChallan" onChange={handleFileChange} />
              </div>
              <div className="sdf-drop">
                <h4>Weighment Slip</h4>
                <p>PDF, JPG, PNG up to 10MB</p>
                <input type="file" name="weighmentSlip" onChange={handleFileChange} />
              </div>
              <div className="sdf-drop" style={{ gridColumn: "1 / -1" }}>
                <h4>Loading Area Photos</h4>
                <p>Capture or upload photos of the loaded vehicle</p>
                <input type="file" name="loadingPhotos" onChange={handleFileChange} multiple />
              </div>
            </div>
          </section>
        </form>
      </div>

      <div className="sdf-form-actions" style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button type="button" className="sdf-btn-outline" onClick={saveDraft} disabled={loading}>
          {loading ? <Spinner size="sm" /> : "Save Draft"}
        </button>
        <button type="button" className="sdf-btn-primary" onClick={submitDispatch} disabled={loading}>
          {loading ? <Spinner size="sm" /> : "Submit for Approval"}
        </button>
      </div>
    </div>
  );
};

export default SupplierDispatchForm;
